import React, { useRef } from 'react';
import { Box, Container } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import styled from 'styled-components';

const AboutSection = styled(Box)`
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #030b17 0%, #0a1929 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
`;

const BackgroundPattern = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(rgba(79, 172, 254, 0.03) 2px, transparent 2px);
  background-size: 30px 30px;
  z-index: 0;
`;

const FloatingShape = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(79, 172, 254, 0.03) 0%, rgba(0, 242, 254, 0.01) 100%);
  z-index: 0;
`;

const ContentWrapper = styled(Box)`
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3.5rem;
  color: #f5f5f5;
  margin-bottom: 1.5rem;
  text-align: center;
  position: relative;
  display: inline-block;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, #4facfe, #00f2fe);
    border-radius: 2px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 800px;
  text-align: center;
  margin: 0 auto;
  line-height: 1.6;
  padding: 0 1rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0 1.5rem;
  }
`;

const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.5], [0, 1]);
  
  return (
    <AboutSection id="about" ref={ref}>
      <BackgroundPattern />
      
      <FloatingShape 
        style={{ 
          width: '500px', 
          height: '500px', 
          top: '-10%', 
          right: '-10%',
          opacity: 0.6
        }}
        animate={{ 
          y: [0, -30, 0],
          rotate: [0, 5, 0],
          opacity: [0.6, 0.8, 0.6]
        }}
        transition={{ 
          duration: 20,
          repeat: Infinity,
          repeatType: 'reverse'
        }}
      />
      
      <FloatingShape 
        style={{ 
          width: '300px', 
          height: '300px', 
          bottom: '10%', 
          left: '5%',
          opacity: 0.4
        }}
        animate={{ 
          y: [0, 30, 0],
          rotate: [0, -5, 0],
          opacity: [0.4, 0.6, 0.4]
        }}
        transition={{ 
          duration: 15,
          repeat: Infinity,
          repeatType: 'reverse'
        }}
      />
      
      <Container maxWidth="lg" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
        <ContentWrapper>
          <motion.div style={{ opacity, width: '100%', textAlign: 'center' }}>
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
              <SectionTitle>Our Vision</SectionTitle>
              <SectionSubtitle>
                At Yurix, we don't just implement technology—we architect transformative solutions that redefine what's possible. Our multidisciplinary approach unites advanced AI, data science, and domain expertise to solve complex challenges and create sustainable competitive advantages for forward-thinking organizations.
              </SectionSubtitle>
            </Box>
          </motion.div>
        </ContentWrapper>
      </Container>
    </AboutSection>
  );
};

export default About; 